/* eslint-disable array-callback-return */
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

import SvgPanZoom from "svg-pan-zoom";

import YRMapBelgium from "components/year-review/components/map/YRMapBelgium";
import YRMapWorld from "components/year-review/components/map/YRMapWorld";
import YRMapLegend from "components/year-review/components/map/YRMapLegend";

import FormatGraphDataService from "components/year-review/services/FormatGraphDataService";

import Colors from "components/year-review/constants/colors";

const YRMap = ({ data, style }) => {
  const yrMap = useRef(null);
  const yrMapTooltip = useRef(null);

  const [mapData, setMapData] = useState(null);

  const [svgPanZoom, setSvgPanzoom] = useState(null);
  const [svgMapZoomLevel, setSvgMapZoomLevel] = useState(1);

  const yearReviewContainer = document.querySelector("[data-year-review]");
  const { lang } = yearReviewContainer.dataset;

  const isWorldMap = style.country === "world";

  let prefix = "in";
  let suffix = isWorldMap ? "% of total" : " per 100 inhabitants";

  if (lang && lang === "fr") {
    prefix = "en";
    suffix = isWorldMap ? "% du total" : " par 100 habitants";
  } else if (lang && lang === "nl") {
    prefix = "in";
    suffix = isWorldMap ? "% van het totaal" : " per 100 inwoners";
  }

  const legendData = [
    {
      min: isWorldMap ? 1 : 2,
      max: isWorldMap ? 10 : 5,
    },
    {
      min: isWorldMap ? 10 : 5,
      max: isWorldMap ? 100 : 10,
    },
    {
      min: isWorldMap ? 100 : 10,
      max: isWorldMap ? 10000 : 25,
    },
    {
      min: isWorldMap ? 10000 : 25,
      max: "",
    },
  ];

  useEffect(() => {
    const fetchMapData = async () => {
      const { year } = yearReviewContainer.dataset;
      const { data } = await import(
        `components/year-review/assets/json/${year}/data-map-${style.country}.json`
      );

      setMapData(data);
    };

    fetchMapData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const yrMapEl = yrMap.current;

    if (!yrMap || !mapData) return;

    data.forEach((mapPart) => {
      const mapPartPath = yrMapEl.querySelector(`[data-id='${mapPart.code}']`);
      const mapPartValue = mapData[mapPart.code];

      let mapPartValueFactor = null;
      let color = null;

      if (typeof mapPartValue === "object") {
        const { valueTotal } = mapPartValue;

        mapPartValueFactor = valueTotal;
      } else {
        mapPartValueFactor = mapPartValue;
      }

      if (
        mapPartValueFactor > legendData[0].min &&
        mapPartValueFactor <= legendData[0].max
      ) {
        color = Colors.dnsBlue;
      } else if (
        mapPartValueFactor > legendData[1].min &&
        mapPartValueFactor <= legendData[1].max
      ) {
        color = Colors.dnsGreen;
      } else if (
        mapPartValueFactor > legendData[2].min &&
        mapPartValueFactor <= legendData[2].max
      ) {
        color = Colors.dnsDarkBlue;
      } else if (mapPartValueFactor > legendData[3].min) {
        color = Colors.dnsBlack;
      }

      if (mapPartPath && color) {
        mapPartPath.style.fill = color;
      }
    });
  }, [yrMap, mapData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const yrMapSvgEl = yrMap.current.querySelector("svg");

    if (!yrMapSvgEl) return;

    setSvgPanzoom(
      SvgPanZoom(yrMapSvgEl, {
        controlIconsEnabled: false,
        zoomEnabled: false,
        preventMouseEventsDefault: false,
        dblClickZoomEnabled: false,
        mouseWheelZoomEnabled: false,
        center: 1,
        fit: 1,
        panEnabled: svgMapZoomLevel !== 1,
        zoom: svgMapZoomLevel,
      })
    );
  }, [yrMap]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!yrMap || !mapData) return;

    const yrMapSvgEl = yrMap.current.querySelector("svg");
    const yrMapTooltipEl = yrMapTooltip.current;

    const onMapPathMouseOver = (event) => {
      const path = event.currentTarget;
      const { id } = path.dataset;
      const currentMapPart = data.find((mapPart) => mapPart.code === id);
      const { name } = currentMapPart;
      let tooltipContent = "";

      const mapPartData = mapData[id];

      if (typeof mapPartData === "object") {
        const { valuePercentage, valueTotal } = mapPartData;

        tooltipContent = `<b>${FormatGraphDataService.addCommas(
          valueTotal
        )}<br/>${FormatGraphDataService.twoDecimalPlaces(
          valuePercentage
        )}${suffix}</b>
                                    <br/>${prefix} ${name}`;
      } else {
        tooltipContent = `<b>${FormatGraphDataService.twoDecimalPlaces(
          mapPartData
        )}${suffix}</b>
                                    <br/>${prefix} ${name}`;
      }

      const {
        top: pathTop,
        left: pathLeft,
        width: pathWidth,
        height: pathHeight,
      } = path.getBoundingClientRect();

      const toolTipTop = pathTop + pathWidth * 0.5 + 5;
      const toolTipLeft = pathLeft + pathHeight * 0.5 + 5;

      yrMapTooltipEl.style.top = `${toolTipTop}px`;
      yrMapTooltipEl.style.left = `${toolTipLeft}px`;
      yrMapTooltipEl.style.opacity = 1;

      yrMapTooltipEl.innerHTML = tooltipContent;
    };

    const onMapPathMouseOut = () => {
      yrMapTooltipEl.style.opacity = 0;
    };

    yrMapSvgEl.querySelectorAll("path").forEach((mapPath) => {
      mapPath.addEventListener("mouseover", onMapPathMouseOver);
      mapPath.addEventListener("mouseout", onMapPathMouseOut);
    });

    return () => {
      yrMapSvgEl.querySelectorAll("path").forEach((mapPath) => {
        mapPath.removeEventListener("mouseover", onMapPathMouseOver);
        mapPath.removeEventListener("mouseout", onMapPathMouseOut);
      });
    };
  }, [yrMap, yrMapTooltip, mapData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!svgPanZoom) return;

    svgPanZoom.zoom(svgMapZoomLevel);

    if (svgMapZoomLevel === 1) {
      svgPanZoom.disablePan();
      svgPanZoom.resetPan();
    } else {
      svgPanZoom.enablePan();
    }
  }, [svgMapZoomLevel]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className={`yr-map${
        style && style.country ? ` is-${style.country}` : ""
      }`}
      ref={yrMap}
    >
      {style && style.country && style.country === "belgium" ? (
        <YRMapBelgium />
      ) : (
        <YRMapWorld />
      )}

      <div className="yr-map__controls">
        <button
          className="yr-map__control yr-map__zoom-out"
          type="button"
          disabled={svgMapZoomLevel < 3}
          onClick={() => setSvgMapZoomLevel(1)}
        >
          &minus;
        </button>
        <button
          className="yr-map__control yr-map__zoom-in"
          type="button"
          disabled={svgMapZoomLevel >= 5}
          onClick={() => {
            if (style.country) {
              setSvgMapZoomLevel(3);
            } else {
              setSvgMapZoomLevel(5);
            }
          }}
        >
          &#43;
        </button>
      </div>

      <YRMapLegend legendData={legendData} />

      <div className="yr-map__tooltip yr-tooltip is-small" ref={yrMapTooltip} />
    </div>
  );
};

YRMap.defaultProps = {
  style: {},
};

YRMap.propTypes = {
  data: PropTypes.arrayOf(PropTypes.any).isRequired,
  style: PropTypes.objectOf(PropTypes.any),
};

export default YRMap;
